import { useState } from "react";
import { useSupabaseCallback, useSupabaseLoader } from "../../server/supabase/hooks";
import { toISO8601 } from "../../utils/iso8601";
import { useClaims } from "../generic/hooks/use-claims";
import { dataSharingAgreement } from "./assistants";

import type { SupabaseHandlerArg } from "../../server/supabase/hooks/types";
import type { ISO8601 } from "../../utils/iso8601";
import type { UUID } from "../../utils/uuid";
import type { Assistant } from "./assistants";

type Supabase = SupabaseHandlerArg<"public">["supabase"];

interface DataSharingAcceptance {
  acceptedAt: ISO8601;
  acceptedByUserId: string;
  acceptedByName: string | null;
}

export interface DataSharing {
  agreementId: UUID;
  versionId: UUID;
  acceptance: DataSharingAcceptance | null;
  // The account's hard opt-out: no employee data reaches an assistant, so there is nothing to agree.
  piiOptedOut: boolean;
}

export interface AssistantSetup {
  startedAt: ISO8601;
  completedAt: ISO8601 | null;
}

export interface McpConnectStatus {
  clientAccount: boolean;
  dataSharing: DataSharing;
  setups: Partial<Record<Assistant, AssistantSetup>>;
}

const UNIQUE_VIOLATION = "23505";

const setupConflict = { onConflict: "account_id,user_id,assistant" };

const acceptedByName = async (
  supabase: Supabase,
  accountId: UUID,
  userId: string,
): Promise<string | null> => {
  const { data } = await supabase
    .from("user_profile_data")
    .select("person_first_name,person_last_name,email")
    .eq("id", userId)
    .eq("account_id", accountId)
    .limit(1)
    .maybeSingle();
  const name = [data?.person_first_name, data?.person_last_name].filter(Boolean).join(" ");
  return name || data?.email || null;
};

const loadMcpConnectStatus = async (
  supabase: Supabase,
  accountId: UUID,
): Promise<McpConnectStatus> => {
  const { data: agreement, error: agreementError } = await supabase
    .from("clickwrap_agreement")
    .select("id,current_version_id")
    .eq("key", "ai_pii")
    .single();
  if (agreementError) throw agreementError;
  if (!agreement.current_version_id) throw new Error("Current agreement version was not found.");

  const [account, acceptance, setups] = await Promise.all([
    supabase.from("account").select("usage,enable_ai_pii").eq("id", accountId).single(),
    supabase
      .from("account_clickwrap_acceptance")
      .select("accepted_at,accepted_by_user_id")
      .eq("agreement_id", agreement.id)
      .eq("agreement_version_id", agreement.current_version_id)
      .is("revoked_at", null)
      .maybeSingle(),
    supabase.from("mcp_assistant_setup").select("assistant,started_at,completed_at"),
  ]);
  if (account.error) throw account.error;
  if (acceptance.error) throw acceptance.error;
  if (setups.error) throw setups.error;

  return {
    clientAccount: account.data.usage === "CLIENT",
    dataSharing: {
      agreementId: agreement.id,
      versionId: agreement.current_version_id,
      acceptance: acceptance.data
        ? {
            acceptedAt: acceptance.data.accepted_at,
            acceptedByUserId: acceptance.data.accepted_by_user_id,
            acceptedByName: await acceptedByName(
              supabase,
              accountId,
              acceptance.data.accepted_by_user_id,
            ),
          }
        : null,
      piiOptedOut: !account.data.enable_ai_pii,
    },
    setups: Object.fromEntries(
      setups.data.map((setup) => [
        setup.assistant,
        { startedAt: setup.started_at, completedAt: setup.completed_at },
      ]),
    ),
  };
};

export const agreeToDataSharing = async (
  supabase: Supabase,
  dataSharing: DataSharing,
  assistant: Assistant,
): Promise<boolean> => {
  let recorded = false;
  if (!dataSharing.acceptance && !dataSharing.piiOptedOut) {
    const { error } = await supabase.from("account_clickwrap_acceptance").insert({
      agreement_id: dataSharing.agreementId,
      agreement_version_id: dataSharing.versionId,
      snapshot_content_format: "plain_text",
      snapshot_content_text: dataSharingAgreement(assistant).join("\n\n"),
    });
    if (error && error.code !== UNIQUE_VIOLATION) throw error;
    recorded = !error;
  }

  const { error } = await supabase
    .from("mcp_assistant_setup")
    .upsert({ assistant }, { ...setupConflict, ignoreDuplicates: true });
  if (error) throw error;

  return recorded;
};

export const completeSetup = async (supabase: Supabase, assistant: Assistant): Promise<void> => {
  const { error } = await supabase
    .from("mcp_assistant_setup")
    .upsert({ assistant, completed_at: toISO8601(new Date()) }, setupConflict);
  if (error) throw error;
};

export const resetSetup = async (supabase: Supabase, assistant: Assistant): Promise<void> => {
  const { error } = await supabase.from("mcp_assistant_setup").delete().eq("assistant", assistant);
  if (error) throw error;
};

export interface UseMcpConnectStatus {
  status: McpConnectStatus | null;
  loading: boolean;
  error: Error | null;
  userId: string | undefined;
  agree: (assistant: Assistant) => Promise<boolean | undefined>;
  complete: (assistant: Assistant) => Promise<void | undefined>;
  reset: (assistant: Assistant) => Promise<void | undefined>;
}

export const useMcpConnectStatus = (): UseMcpConnectStatus => {
  const claims = useClaims();
  const loader = useSupabaseLoader(
    async ({ supabase, account_id }) => await loadMcpConnectStatus(supabase, account_id),
    [],
  );
  const [refreshed, setRefreshed] = useState<McpConnectStatus | null>(null);
  const status = refreshed ?? loader.data;

  const agree = useSupabaseCallback(
    async ({ supabase, account_id }, assistant: Assistant) => {
      if (!status) throw new Error("Data sharing status is not loaded.");
      const recorded = await agreeToDataSharing(supabase, status.dataSharing, assistant);
      setRefreshed(await loadMcpConnectStatus(supabase, account_id));
      return recorded;
    },
    [status],
  );

  const complete = useSupabaseCallback(async ({ supabase, account_id }, assistant: Assistant) => {
    await completeSetup(supabase, assistant);
    setRefreshed(await loadMcpConnectStatus(supabase, account_id));
  }, []);

  const reset = useSupabaseCallback(async ({ supabase, account_id }, assistant: Assistant) => {
    await resetSetup(supabase, assistant);
    setRefreshed(await loadMcpConnectStatus(supabase, account_id));
  }, []);

  return {
    status,
    loading: !status && loader.loading,
    error: status ? null : loader.error,
    userId: claims?.sub,
    agree,
    complete,
    reset,
  };
};
